import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getSafetyEventsList,
  saveSafetyEventRow,
  deleteSafetyEventRow,
  getActivitiesList,
  getSWAEventsList,
  getPermitsList,
  getJSAEvaluationsList,
  type SafetyEventRow,
  type SafetyEventType,
  type PotentialSeverity,
  type ActivityRow,
  type SWAEventRow,
  type PermitRow,
  type JSAEvaluationRow,
} from '@/services/db/dsrEntry';
import PhotoUploader from '@/components/dsr/PhotoUploader';
import CreateActionItemButton from './CreateActionItemButton';
import {
  FieldLabel,
  FieldError,
  ChipGroup,
  MultiChipGroup,
  formatDateTimeLocal,
  fromDateTimeLocal,
} from './_shared';

const EVENT_TYPE_OPTIONS: { value: SafetyEventType; label: string; color: string }[] = [
  { value: 'near_miss', label: 'Near Miss', color: '#00b4b4' },
  { value: 'first_aid', label: 'First Aid', color: '#1668a0' },
  { value: 'recordable_injury', label: 'Recordable Injury', color: '#d87820' },
  { value: 'lost_time_injury', label: 'Lost Time Injury', color: '#c0392b' },
  { value: 'property_damage', label: 'Property Damage', color: '#6b7280' },
  { value: 'environmental_release', label: 'Environmental Release', color: '#3ba870' },
  { value: 'security_incident', label: 'Security Incident', color: '#0d2235' },
];

const CATEGORY_OPTIONS = [
  { value: 'personnel', label: 'Personnel' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'environment', label: 'Environment' },
  { value: 'process', label: 'Process' },
  { value: 'organizational', label: 'Organizational' },
  { value: 'external', label: 'External' },
] as const;

const POTENTIAL_SEVERITY_OPTIONS: { value: PotentialSeverity; label: string; color: string }[] = [
  { value: 'minor', label: 'Minor', color: '#00b4b4' },
  { value: 'moderate', label: 'Moderate', color: '#1668a0' },
  { value: 'serious', label: 'Serious', color: '#d87820' },
  { value: 'critical', label: 'Critical', color: '#c0392b' },
  { value: 'catastrophic', label: 'Catastrophic', color: '#7b1fa2' },
];

const INJURY_EVENT_TYPES: SafetyEventType[] = [
  'first_aid',
  'recordable_injury',
  'lost_time_injury',
];

const entrySchema = z
  .object({
    event_type: z.string().min(1, 'Event type is required'),
    event_datetime: z.string().min(1, 'Date and time is required'),
    location_area: z.string().min(1, 'Location is required').max(200),
    event_title: z.string().min(1, 'Event title is required').max(200),
    event_description: z.string().min(1, 'Event description is required').max(2000),
    immediate_actions: z.string().min(1, 'Immediate actions is required').max(500),
    category: z.array(z.string()).min(1, 'At least one category is required'),
    is_work_stopped: z.boolean(),
    personnel_count: z
      .string()
      .min(1, 'Personnel count is required')
      .regex(/^\d+$/, 'Must be 0 or more'),
    activity_entry_id: z.string().optional(),
    activity_description: z.string().max(300).optional(),
    swa_event_id: z.string().optional(),
    permit_entry_id: z.string().optional(),
    jsa_evaluation_id: z.string().optional(),
    contractor_crew: z.string().max(200).optional(),
    is_supervisor_notified: z.boolean(),
    is_management_notified: z.boolean(),
    is_regulatory_required: z.boolean(),
    near_miss_potential_severity: z.string().optional(),
    near_miss_conditions: z.string().max(500).optional(),
    injured_person_role: z.string().optional(),
    injury_type: z.string().optional(),
    body_part_affected: z.string().optional(),
    treatment_provided: z.string().max(500).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.event_type === 'near_miss' && !data.near_miss_potential_severity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Potential severity is required for Near Miss events',
        path: ['near_miss_potential_severity'],
      });
    }
  });

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  event_type: 'near_miss',
  event_datetime: formatDateTimeLocal(new Date().toISOString()),
  location_area: '',
  event_title: '',
  event_description: '',
  immediate_actions: '',
  category: ['personnel'],
  is_work_stopped: false,
  personnel_count: '0',
  activity_entry_id: '',
  activity_description: '',
  swa_event_id: '',
  permit_entry_id: '',
  jsa_evaluation_id: '',
  contractor_crew: '',
  is_supervisor_notified: false,
  is_management_notified: false,
  is_regulatory_required: false,
  near_miss_potential_severity: '',
  near_miss_conditions: '',
  injured_person_role: '',
  injury_type: '',
  body_part_affected: '',
  treatment_provided: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function SafetyEventsForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: SafetyEventRow }>('closed');

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_safety_events', dsrId],
    queryFn: () => getSafetyEventsList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });
  const { data: swaEvents = [] } = useQuery({
    queryKey: ['entry_swa', dsrId],
    queryFn: () => getSWAEventsList(dsrId),
    staleTime: 30 * 1000,
  });
  const { data: permits = [] } = useQuery({
    queryKey: ['entry_permits', dsrId],
    queryFn: () => getPermitsList(dsrId),
    staleTime: 30 * 1000,
  });
  const { data: jsaEvaluations = [] } = useQuery({
    queryKey: ['entry_jsa_evaluations', dsrId],
    queryFn: () => getJSAEvaluationsList(dsrId),
    staleTime: 30 * 1000,
  });

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_safety_events', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this safety event?')) return;
    try {
      await deleteSafetyEventRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_safety_events', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete event');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Safety Events</h2>
        <p className="text-xs text-muted-foreground">
          Near misses, injuries, property damage, environmental releases, and security incidents.
        </p>
      </div>

      {/* Why (LAP-004): non-dismissible legal notice for regulatory reporting awareness.
          Crawl-exit criterion 3.8. Must remain visible at all times — do not make dismissible. */}
      <div className="flex items-start gap-3 rounded-md border-l-4 border-mozaic-orange bg-mozaic-orange/10 p-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-mozaic-orange" />
        <p className="text-xs text-mozaic-navy">
          <strong>Regulatory notice:</strong> Safety event records may be subject to regulatory
          reporting requirements. Ensure all information is accurate and complete before
          submission. Consult your safety manager for incidents requiring regulatory notification.
        </p>
      </div>

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No safety events recorded.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <SafetyEventCard
          key={entry.id}
          entry={entry}
          dsrId={dsrId}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <SafetyEventEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          activities={activities}
          swaEvents={swaEvents}
          permits={permits}
          jsaEvaluations={jsaEvaluations}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => setMode('adding')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add Safety Event
        </Button>
      )}
    </div>
  );
}

function SafetyEventCard({
  entry,
  dsrId,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: SafetyEventRow;
  dsrId: string;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const meta = EVENT_TYPE_OPTIONS.find((o) => o.value === entry.event_type);
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex flex-wrap gap-2">
          {meta && (
            <Badge style={{ backgroundColor: meta.color }} className="text-white">
              {meta.label.toUpperCase()}
            </Badge>
          )}
          {entry.is_work_stopped && (
            <Badge className="bg-mozaic-red text-white">WORK STOPPED</Badge>
          )}
          {entry.is_regulatory_required && (
            <Badge className="bg-mozaic-orange text-white">REGULATORY</Badge>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {new Date(entry.event_datetime).toLocaleString()}
          </span>
        </div>
        <h3 className="font-semibold text-mozaic-navy">{entry.event_title}</h3>
        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{entry.event_description}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {entry.location_area} · {entry.personnel_count} personnel
        </p>
        <PhotoUploader
          entityType="safety_event"
          entityId={entry.id}
          dsrId={dsrId}
          readOnly={isReadOnly}
        />
        {!isReadOnly && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
            <div className="ml-auto">
              <CreateActionItemButton
                dsrId={dsrId}
                sourceSection="safety_events"
                defaultDescription={`Follow-up: ${entry.event_title}`}
                defaultPriority="high"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SafetyEventEntryForm({
  dsrId,
  tenantId,
  activities,
  swaEvents,
  permits,
  jsaEvaluations,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  activities: ActivityRow[];
  swaEvents: SWAEventRow[];
  permits: PermitRow[];
  jsaEvaluations: JSAEvaluationRow[];
  editing?: SafetyEventRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          event_type: editing.event_type,
          event_datetime: formatDateTimeLocal(editing.event_datetime),
          location_area: editing.location_area,
          event_title: editing.event_title,
          event_description: editing.event_description,
          immediate_actions: editing.immediate_actions,
          category: editing.category ?? ['personnel'],
          is_work_stopped: editing.is_work_stopped,
          personnel_count: String(editing.personnel_count),
          activity_entry_id: editing.activity_entry_id ?? '',
          activity_description: editing.activity_description ?? '',
          swa_event_id: editing.swa_event_id ?? '',
          permit_entry_id: editing.permit_entry_id ?? '',
          jsa_evaluation_id: editing.jsa_evaluation_id ?? '',
          contractor_crew: editing.contractor_crew ?? '',
          is_supervisor_notified: editing.is_supervisor_notified,
          is_management_notified: editing.is_management_notified,
          is_regulatory_required: editing.is_regulatory_required,
          near_miss_potential_severity: editing.near_miss_potential_severity ?? '',
          near_miss_conditions: editing.near_miss_conditions ?? '',
          injured_person_role: editing.injured_person_role ?? '',
          injury_type: editing.injury_type ?? '',
          body_part_affected: editing.body_part_affected ?? '',
          treatment_provided: editing.treatment_provided ?? '',
        }
      : EMPTY,
  });

  const eventType = watch('event_type') as SafetyEventType;
  const isInjury = INJURY_EVENT_TYPES.includes(eventType);
  const isNearMiss = eventType === 'near_miss';

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveSafetyEventRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        eventType: values.event_type as SafetyEventType,
        eventDatetime: fromDateTimeLocal(values.event_datetime),
        locationArea: values.location_area,
        eventTitle: values.event_title,
        eventDescription: values.event_description,
        immediateActions: values.immediate_actions,
        category: values.category,
        isWorkStopped: values.is_work_stopped,
        personnelCount: parseInt(values.personnel_count, 10),
        activityEntryId: values.activity_entry_id || null,
        activityDescription: values.activity_description || null,
        swaEventId: values.swa_event_id || null,
        permitEntryId: values.permit_entry_id || null,
        jsaEvaluationId: values.jsa_evaluation_id || null,
        contractorCrew: values.contractor_crew || null,
        isSupervisorNotified: values.is_supervisor_notified,
        isManagementNotified: values.is_management_notified,
        isRegulatoryRequired: values.is_regulatory_required,
        nearMissPotentialSeverity: isNearMiss
          ? (values.near_miss_potential_severity as PotentialSeverity)
          : null,
        nearMissConditions: isNearMiss ? values.near_miss_conditions || null : null,
        injuredPersonRole: isInjury ? values.injured_person_role || null : null,
        injuryType: isInjury ? values.injury_type || null : null,
        bodyPartAffected: isInjury ? values.body_part_affected || null : null,
        treatmentProvided: isInjury ? values.treatment_provided || null : null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save safety event');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Safety Event' : 'New Safety Event'}</h3>

        <div className="space-y-2">
          <FieldLabel required>Event Type</FieldLabel>
          <Controller
            control={control}
            name="event_type"
            render={({ field }) => (
              <ChipGroup
                options={EVENT_TYPE_OPTIONS}
                value={field.value as SafetyEventType}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.event_type?.message} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel required>Event Date / Time</FieldLabel>
            <Controller
              control={control}
              name="event_datetime"
              render={({ field }) => <Input {...field} type="datetime-local" />}
            />
            <FieldError message={errors.event_datetime?.message} />
          </div>
          <div className="space-y-2">
            <FieldLabel required>Location / Area</FieldLabel>
            <Controller
              control={control}
              name="location_area"
              render={({ field }) => (
                <Input {...field} maxLength={200} placeholder="Where did it happen?" />
              )}
            />
            <FieldError message={errors.location_area?.message} />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel required>Event Title</FieldLabel>
          <Controller
            control={control}
            name="event_title"
            render={({ field }) => (
              <Input {...field} maxLength={200} placeholder="Short headline" />
            )}
          />
          <FieldError message={errors.event_title?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Event Description</FieldLabel>
          <Controller
            control={control}
            name="event_description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={4}
                maxLength={2000}
                placeholder="What happened? Be specific and factual."
              />
            )}
          />
          <FieldError message={errors.event_description?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Immediate Actions Taken</FieldLabel>
          <Controller
            control={control}
            name="immediate_actions"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={3}
                maxLength={500}
                placeholder="What did you do right after?"
              />
            )}
          />
          <FieldError message={errors.immediate_actions?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Category (select all that apply)</FieldLabel>
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <MultiChipGroup
                options={CATEGORY_OPTIONS}
                value={field.value as string[]}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.category?.message as string | undefined} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel required>Personnel Affected</FieldLabel>
            <Controller
              control={control}
              name="personnel_count"
              render={({ field }) => (
                <Input {...field} type="number" inputMode="numeric" min={0} placeholder="0" />
              )}
            />
            <FieldError message={errors.personnel_count?.message} />
          </div>
          <div className="space-y-2">
            <FieldLabel>Contractor / Crew</FieldLabel>
            <Controller
              control={control}
              name="contractor_crew"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} maxLength={200} />
              )}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Controller
            control={control}
            name="is_work_stopped"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                Work Stopped
              </label>
            )}
          />
          <Controller
            control={control}
            name="is_supervisor_notified"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                Supervisor Notified
              </label>
            )}
          />
          <Controller
            control={control}
            name="is_management_notified"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                Management Notified
              </label>
            )}
          />
          <Controller
            control={control}
            name="is_regulatory_required"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                Regulatory Reporting Required
              </label>
            )}
          />
        </div>

        {isNearMiss && (
          <>
            <div className="space-y-2">
              <FieldLabel required>Potential Severity</FieldLabel>
              <Controller
                control={control}
                name="near_miss_potential_severity"
                render={({ field }) => (
                  <ChipGroup
                    options={POTENTIAL_SEVERITY_OPTIONS}
                    value={field.value as PotentialSeverity}
                    onChange={(v) => field.onChange(v)}
                    wrap
                  />
                )}
              />
              <FieldError message={errors.near_miss_potential_severity?.message} />
            </div>
            <div className="space-y-2">
              <FieldLabel>Conditions / Factors</FieldLabel>
              <Controller
                control={control}
                name="near_miss_conditions"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={2}
                    maxLength={500}
                    placeholder="Conditions that contributed"
                  />
                )}
              />
            </div>
          </>
        )}

        {isInjury && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              Injury Details
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <FieldLabel>Injured Person Role</FieldLabel>
                <Controller
                  control={control}
                  name="injured_person_role"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder="e.g. Roustabout"
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <FieldLabel>Injury Type</FieldLabel>
                <Controller
                  control={control}
                  name="injury_type"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder="e.g. Strain, laceration"
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <FieldLabel>Body Part</FieldLabel>
                <Controller
                  control={control}
                  name="body_part_affected"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder="e.g. Left hand"
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <FieldLabel>Treatment Provided</FieldLabel>
                <Controller
                  control={control}
                  name="treatment_provided"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      maxLength={500}
                      placeholder="Treatment given on-site"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {/* Cross-references — only show when there's something to link to */}
        {(activities.length > 0 || swaEvents.length > 0 || permits.length > 0 || jsaEvaluations.length > 0) && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              Cross-references (optional)
            </p>
            {activities.length > 0 && (
              <div className="space-y-2">
                <FieldLabel>Linked Activity</FieldLabel>
                <Controller
                  control={control}
                  name="activity_entry_id"
                  render={({ field }) => (
                    <ChipGroup
                      options={[
                        { value: '', label: 'None' },
                        ...activities.map((a) => ({
                          value: a.id,
                          label:
                            a.description.length > 30
                              ? a.description.slice(0, 30) + '…'
                              : a.description,
                        })),
                      ]}
                      value={field.value ?? ''}
                      onChange={(v) => field.onChange(v)}
                      wrap
                    />
                  )}
                />
              </div>
            )}
            {swaEvents.length > 0 && (
              <div className="space-y-2">
                <FieldLabel>Linked SWA Event</FieldLabel>
                <Controller
                  control={control}
                  name="swa_event_id"
                  render={({ field }) => (
                    <ChipGroup
                      options={[
                        { value: '', label: 'None' },
                        ...swaEvents.map((s) => ({
                          value: s.id,
                          label:
                            s.site_location.length > 30
                              ? s.site_location.slice(0, 30) + '…'
                              : s.site_location,
                        })),
                      ]}
                      value={field.value ?? ''}
                      onChange={(v) => field.onChange(v)}
                      wrap
                    />
                  )}
                />
              </div>
            )}
            {permits.length > 0 && (
              <div className="space-y-2">
                <FieldLabel>Linked Permit</FieldLabel>
                <Controller
                  control={control}
                  name="permit_entry_id"
                  render={({ field }) => (
                    <ChipGroup
                      options={[
                        { value: '', label: 'None' },
                        ...permits.map((p) => ({
                          value: p.id,
                          label: p.permit_number,
                        })),
                      ]}
                      value={field.value ?? ''}
                      onChange={(v) => field.onChange(v)}
                      wrap
                    />
                  )}
                />
              </div>
            )}
            {jsaEvaluations.length > 0 && (
              <div className="space-y-2">
                <FieldLabel>Linked JSA Evaluation</FieldLabel>
                <Controller
                  control={control}
                  name="jsa_evaluation_id"
                  render={({ field }) => (
                    <ChipGroup
                      options={[
                        { value: '', label: 'None' },
                        ...jsaEvaluations.map((j) => ({
                          value: j.id,
                          label:
                            j.jsa_reference.length > 30
                              ? j.jsa_reference.slice(0, 30) + '…'
                              : j.jsa_reference,
                        })),
                      ]}
                      value={field.value ?? ''}
                      onChange={(v) => field.onChange(v)}
                      wrap
                    />
                  )}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Event'
            ) : (
              'Save Event'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
